import { colors, spacing } from '@ardoq/design-tokens';
import { Label } from '@ardoq/forms';
import { Stack } from '@ardoq/layout';
import type { TourAnchorId } from '@ardoq/product-tours';
import styled from 'styled-components';
import { DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';
import { SurveyHelpText } from '../SurveyHelpText';
import { QuestionContentWrapper } from './questionsTypes';
import {
  isAnswerRequired,
  isQuestionAnswered,
} from '../utils/answerPredicates';
import { PriorityWrapper } from './PriorityWrapper';
import type { ReactNode } from 'react';
import type { PersistedSurveyQuestion } from '@ardoq/api-types';

export const SurveyErrorMessage = styled.span`
  color: ${colors.textError};
  line-height: ${spacing.s16};
  font-size: 12px;
  div:first-child {
    border-color: ${colors.borderError} !important;
  }
`;

export const SurveyWarningMessage = styled.span`
  color: ${colors.textWarning};
  line-height: ${spacing.s16};
  font-size: 12px;
  div:first-child {
    border-color: ${colors.borderWarning} !important;
  }
`;

export const SurveyReferenceAndDependentQuestionField = styled.div<{
  $isDisabled: boolean;
  $isCollapsed: boolean;
}>`
  > div {
    margin: 0 ${spacing.s24};
  }
  background-color: ${colors.surfaceDefault};
  ${({ $isDisabled }) =>
    $isDisabled && `background-color: ${colors.surfaceDisabled};`}
  ${({ $isCollapsed }) => $isCollapsed && 'display: none;'}
  border: solid 1px ${colors.borderSubtle};
  border-top: none;
  position: relative;
  overflow: visible;
  padding: ${spacing.s24} ${spacing.s24};
`;

type QuestionStackProps = {
  question: PersistedSurveyQuestion;
  label: ReactNode;
  dataTourId: TourAnchorId | undefined;
  children: ReactNode;
};

const QuestionStack = ({
  question,
  label,
  dataTourId,
  children,
}: QuestionStackProps) => (
  <Stack
    gap="small"
    flex={1}
    role="group"
    aria-label={question.label}
    data-tour-id={dataTourId}
  >
    {label}
    {question.description ? (
      <SurveyHelpText text={question.description} />
    ) : null}
    {children}
  </Stack>
);

/**
 * The anchor a product tour targets this question by, or `undefined` for the
 * questions no tour step points at.
 */
export type GetQuestionTourAnchorId = (
  question: PersistedSurveyQuestion
) => TourAnchorId | undefined;

export const createQuestionContentWrapper =
  (getQuestionTourAnchorId: GetQuestionTourAnchorId): QuestionContentWrapper =>
  ({
    question,
    answer,
    highlightUnansweredPriorityQuestions,
    i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
    children,
  }) => {
    const dataTourId = getQuestionTourAnchorId(question);
    const isMandatory = Boolean(
      question.validators &&
      question.validators.some(validator => validator.type === 'mandatory')
    );
    const isPriority = isAnswerRequired(question.validators);
    const isAnswered = isQuestionAnswered(answer, question);

    if (isMandatory) {
      return (
        <QuestionStack
          question={question}
          dataTourId={dataTourId}
          label={
            <Label indicator="required" i18nLocale={i18nLocale}>
              {question.label}
            </Label>
          }
        >
          {children}
        </QuestionStack>
      );
    }

    if (isPriority) {
      if (highlightUnansweredPriorityQuestions && !isAnswered) {
        return (
          <PriorityWrapper i18nLocale={i18nLocale}>
            <QuestionStack
              question={question}
              dataTourId={dataTourId}
              label={<Label>{question.label}</Label>}
            >
              {children}
            </QuestionStack>
          </PriorityWrapper>
        );
      }
      return (
        <QuestionStack
          question={question}
          dataTourId={dataTourId}
          label={
            <Label indicator="priority" i18nLocale={i18nLocale}>
              {question.label}
            </Label>
          }
        >
          {children}
        </QuestionStack>
      );
    }

    return (
      <QuestionStack
        question={question}
        dataTourId={dataTourId}
        label={<Label>{question.label}</Label>}
      >
        {children}
      </QuestionStack>
    );
  };

const noTourAnchor = (): undefined => undefined;

export const DefaultQuestionContentWrapper: QuestionContentWrapper =
  createQuestionContentWrapper(noTourAnchor);
